import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { HttpResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { BreadcrumbService } from '@k12/navigation';
import { AppInsightsService } from '@k12/error_handler';
import { AdminUserType, ProgramPartner, ProviderData } from '@k12/models';
import { IUserRecordProviderDetailsService } from '@k12/tokens';
import { Observable, Subject, takeUntil } from 'rxjs';

interface ProgramPartnerListService {
  getAllProgramPartners(): Observable<HttpResponse<ProgramPartner[]>>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,

  selector: 'lib-user-record-create-task',
  imports: [MatButtonModule, MatIconModule, MatProgressBarModule],
  templateUrl: './user-record-create-task.component.html',
  styleUrls: ['./user-record-create-task.component.scss']
})
export class UserRecordCreateTaskComponent implements OnInit, OnDestroy {
  // Input for services that need to be injected from the consuming application
  @Input() userRecordService!: IUserRecordProviderDetailsService;
  @Input() programPartnerService!: ProgramPartnerListService;
  @Input() taskFormComponent: unknown;

  @Output() formSubmit = new EventEmitter<boolean>();

  personaId: number | null = null;
  userId: string | null = null;
  userData: ProviderData | null = null;
  accountId: string | null = null;
  programPartnerTypeId: number | null = null;
  private routeUnsubscribe = new Subject<void>();
  private readonly appInsightsService = inject(AppInsightsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly breadcrumbService = inject(BreadcrumbService);

  ngOnInit(): void {
    // Get userId from route parameters
    this.route.params.pipe(takeUntil(this.routeUnsubscribe)).subscribe((params) => {
      this.userId = params['id'] || null;
      if (this.userId) {
        this.getUserDataForBreadcrumbs();
      }
    });

    if (history.state && history.state['personaId']) {
      this.personaId = history.state['personaId'];
    }

    if (history.state && history.state['providerId']) {
      this.userId = history.state['providerId'];
      this.getUserDataForBreadcrumbs();
    }

    document.documentElement.scrollTop = 0;
  }

  ngOnDestroy(): void {
    this.routeUnsubscribe.next();
    this.routeUnsubscribe.complete();
    this.breadcrumbService.clearDynamicBreadcrumbs();
  }

  // Method to get user data and set breadcrumbs
  private getUserDataForBreadcrumbs(): void {
    const userId = this.userId;
    if (userId && this.programPartnerService) {
      // The userId from route is the programPartnerId (the partner's id)
      // We need to look up the partner to get both programPartnerTypeId and accountId
      this.programPartnerService.getAllProgramPartners().subscribe({
        next: (response: HttpResponse<ProgramPartner[]>) => {
          const partners = response.body ?? [];
          if (partners.length > 0) {
            // Find the partner by id (programPartnerId)
            const partner = partners.find((p) => p.id === userId);

            if (partner) {
              // Get the correct programPartnerTypeId and accountId from the partner
              this.programPartnerTypeId = partner.programPartnerTypeId;
              this.accountId = partner.accountId || userId; // Fallback to userId if accountId not available
            } else {
              // If partner not found, use userId as accountId and try to get type from user data
              this.accountId = userId;
            }
          } else {
            this.accountId = userId;
          }

          // Get user details for breadcrumbs
          if (this.userRecordService) {
            this.userRecordService.getProviderDetails(userId, AdminUserType.Provider).subscribe({
              next: (res: unknown) => {
                this.userData = res as ProviderData;
                this.setBreadcrumbsForCreateTask();
              },
              error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
            });
          }
        },
        error: () => {
          // On error, fallback to using userId as accountId
          this.accountId = userId;
          if (this.userRecordService) {
            this.userRecordService.getProviderDetails(userId, AdminUserType.Provider).subscribe({
              next: (res: unknown) => {
                this.userData = res as ProviderData;
                this.setBreadcrumbsForCreateTask();
              },
              error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
            });
          }
        }
      });
    } else if (userId && this.userRecordService) {
      this.accountId = userId;
      this.userRecordService.getProviderDetails(userId, AdminUserType.Provider).subscribe({
        next: (res: unknown) => {
          this.userData = res as ProviderData;
          this.setBreadcrumbsForCreateTask();
        },
        error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
      });
    }
  }

  // Method to set breadcrumbs for the create task page
  private setBreadcrumbsForCreateTask(): void {
    if (this.userData && this.userData.firstName && this.userData.lastName && this.userId) {
      const userName = `${this.userData.firstName} ${this.userData.lastName}`;
      // Set breadcrumbs for user record, edit, and create task pages
      this.breadcrumbService.setNestedDynamicBreadcrumbs(
        '/work-queue',
        this.userId,
        userName,
        undefined,
        'Create a New Task'
      );
    }
  }

  submitForm(success: boolean): void {
    if (success) {
      this.router.navigate(['/work-queue', this.userId]);
    }
    this.formSubmit.emit(success);
  }
}

